import MasqueradeActiveSession from '@components/MasqueradeActiveSession';
import WorkspaceSyncOverlay from '@components/WorkspaceSyncOverlay';
import { useAuth } from '@contexts/AuthContext';
import { Navigate, Outlet, useLocation, useNavigate, useSearchParams } from 'react-router';
import SubscriptionRequiredModal from '@components/SubscriptionRequiredModal';
import WelcomeModal from '@pages/Home/components/Objectives/WelcomeModal';
import { IS_ANONYMOUS_APP } from '@config/config';
import { useAppSelectors } from '@stores/app/appStore';
import { useEffect, useRef, useState, lazy, Suspense } from 'react';

const DemoIntakeOverlay = lazy(() => import('@pages/Auth/DemoIntake'));


const ProtectedLayout = () => {
  const { isAuthenticated, isAnonymous, masqueradeUser, currentUser, loadUserProfile } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [isSubscriptionModalOpen, setIsSubscriptionModalOpen] = useState(false);
  const eventOpenedRef = useRef(false);
  const demoMode = useAppSelectors.demoMode();

  // Stripe webhook may not have processed by the time the checkout redirect
  // lands, so suppress the paywall and poll until the subscription appears.
  const [awaitingCheckout, setAwaitingCheckout] = useState(
    () => new URLSearchParams(window.location.search).get('checkout_success') === '1',
  );
  const loadUserProfileRef = useRef(loadUserProfile);
  loadUserProfileRef.current = loadUserProfile;

  useEffect(() => {
    if (!awaitingCheckout) return;

    window.history.replaceState({}, '', window.location.pathname);

    let attempt = 0;
    const poll = setInterval(() => {
      attempt++;
      loadUserProfileRef.current(null, true);
      if (attempt >= 10) {
        clearInterval(poll);
        setAwaitingCheckout(false);
      }
    }, 1500);

    return () => clearInterval(poll);
  }, [awaitingCheckout]);

  useEffect(() => {
    if (awaitingCheckout && currentUser?.subscription) {
      setAwaitingCheckout(false);
    }
  }, [awaitingCheckout, currentUser?.subscription]);

  // Post-onboarding welcome modal — shown before subscription modal
  const isPostOnboarding =
    searchParams.get('openWelcomeBrief') === '1' ||
    searchParams.get('onboardingCompleted') === 'true';
  const [showWelcomeFirst, setShowWelcomeFirst] = useState(
    () => isPostOnboarding || localStorage.getItem('leo_welcome_pending') === '1',
  );
  const [postOnboardingHidden, setPostOnboardingHidden] = useState(isPostOnboarding);

  // Detect openWelcomeBrief param set after mount (e.g. demo intake flow)
  // Skip if user already has a subscription — the welcome modal won't render
  // and the subscription cleanup effect would immediately reset showWelcomeFirst,
  // causing an infinite setState loop.
  useEffect(() => {
    if (isPostOnboarding && !showWelcomeFirst && !currentUser?.subscription) {
      setShowWelcomeFirst(true);
      setPostOnboardingHidden(true);
    }
  }, [isPostOnboarding, showWelcomeFirst, currentUser?.subscription]);

  // Clean up onboarding search params and persist flag for post-Stripe redirect
  useEffect(() => {
    if (!isPostOnboarding) return;
    localStorage.setItem('leo_welcome_pending', '1');
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.delete('openWelcomeBrief');
        next.delete('onboardingCompleted');
        return next;
      },
      { replace: true },
    );
  }, [isPostOnboarding, setSearchParams]);

  // Delayed reveal for post-onboarding transition
  useEffect(() => {
    if (!postOnboardingHidden) return;
    const timer = setTimeout(() => setPostOnboardingHidden(false), 2000);
    return () => clearTimeout(timer);
  }, [postOnboardingHidden]);

  // Clean up welcome pending flag when user returns from Stripe with a subscription.
  // The WelcomeModal won't render (user has subscription), so its onClose never fires.
  useEffect(() => {
    if (showWelcomeFirst && currentUser?.subscription) {
      setShowWelcomeFirst(false);
      localStorage.removeItem('leo_welcome_pending');
    }
  }, [showWelcomeFirst, currentUser?.subscription]);

  // Check subscription status and show modal if user is not subscribed
  // Defer subscription modal while welcome modal is still showing
  useEffect(() => {
    if (!isAuthenticated || isAnonymous) {
      setIsSubscriptionModalOpen(false);
      return;
    }

    // Defer subscription modal while welcome flow or checkout polling is active
    if (showWelcomeFirst || awaitingCheckout) {
      setIsSubscriptionModalOpen(false);
      return;
    }

    // Don't auto-close if the modal was explicitly opened via subscription-required event
    if (eventOpenedRef.current) {
      eventOpenedRef.current = false;
      return;
    }

    if (
      currentUser &&
      !currentUser.subscription &&
      location.pathname !== '/onboarding' &&
      !location.pathname.startsWith('/onboarding') &&
      !location.pathname.startsWith('/auth')
    ) {
      setIsSubscriptionModalOpen(true);
    } else {
      setIsSubscriptionModalOpen(false);
    }
  }, [currentUser, isAuthenticated, isAnonymous, location.pathname, showWelcomeFirst, awaitingCheckout]);

  // Listen for subscription-required events from API calls (e.g., chatbot)
  // This ensures the subscription modal is shown consistently across all features
  useEffect(() => {
    const handleSubscriptionRequired = () => {
      console.log('🔔 [ProtectedLayout] Received subscription-required event');
      eventOpenedRef.current = true;
      setIsSubscriptionModalOpen(true);
    };

    window.addEventListener('subscription-required', handleSubscriptionRequired);
    return () => {
      window.removeEventListener('subscription-required', handleSubscriptionRequired);
    };
  }, []);

  // Send toast indicating subscription is not current (active or trialing)
  useEffect(() => {
    if (!isAuthenticated || isAnonymous) {
      return;
    }

    if (
      currentUser &&
      currentUser.subscription &&
      currentUser.subscription.status !== 'active' &&
      currentUser.subscription.status !== 'trialing'
    ) {
      navigate('/settings?tab=billing');
    }
  }, [currentUser, isAuthenticated, isAnonymous, navigate]);

  // Check if this is an OAuth callback (allow it to complete even if onboarding is done)
  const isOAuthCallback =
    location.search.includes('auth_code') ||
    location.search.includes('code') ||
    location.search.includes('state') ||
    location.pathname === '/auth-callback';

  // User just landed from backend redirect after Google/federated sign-in; skip verification
  const isFederatedLoginSuccess =
    new URLSearchParams(location.search).get('federated_login_success') === '1';

  // Anonymous users skip verification and company checks
  if (!isAnonymous) {
    // User signed in but not yet verified -> send to signup verification step (step 3)
    const isVerified =
      currentUser?.confirmation_status === 'VERIFIED';
    if (
      currentUser &&
      !isVerified &&
      currentUser.confirmation_status !== 'FORCE_CHANGE_PASSWORD' &&
      !isOAuthCallback &&
      !isFederatedLoginSuccess
    ) {
      const email =
        (currentUser as { user_name?: string }).user_name ||
        currentUser.name ||
        '';
      const verifyUrl = email
        ? `/auth/signup?step=3&email=${encodeURIComponent(email)}`
        : '/auth/signup?step=3';
      return <Navigate to={verifyUrl} replace />;
    }

    // User signed in but never completed signup (e.g. missing company info) -> send to signup step 4
    // Skip this redirect in the demo app: anonymous sessions are complete by definition;
    // if we ended up here with isAnonymous false, prefer staying in-app over forcing signup.
    const hasCompletedSignup =
      currentUser &&
      (currentUser.companyName || currentUser.companyDisplayName);

    if (
      currentUser &&
      !hasCompletedSignup &&
      !isOAuthCallback &&
      !IS_ANONYMOUS_APP &&
      !demoMode
    ) {
      return (
        <Navigate
          to="/auth/signup?step=4"
          replace
        />
      );
    }
  }

  // Authentication enforcement -- non-demo app redirects to login
  if (!isAuthenticated && !IS_ANONYMOUS_APP && !demoMode) {
    return <Navigate to="/auth/login" replace />;
  }

  // Redirect users who have completed onboarding away from onboarding page
  // But allow OAuth callbacks to complete
  // Handle both /onboarding and /onboarding/ paths
  // Note: Workspace onboarding now uses a modal (WorkspaceOnboardingDrawer) instead of a route
  if (
    currentUser &&
    currentUser.onboard_completed &&
    location.pathname.startsWith('/onboarding') &&
    !isOAuthCallback
  ) {
    return <Navigate to="/" replace />;
  }

  if (
    !isAnonymous &&
    currentUser &&
    (!currentUser.onboard_completed || !('onboard_completed' in currentUser)) &&
    location.pathname !== '/onboarding' &&
    !isOAuthCallback
  ) {
    return <Navigate to="/onboarding" replace />;
  }

  // Special handling for onboarding page - render without sidebar/header
  if (location.pathname.startsWith('/onboarding')) {
    return (
      <>
        <Outlet />
        {masqueradeUser?.id && (
          <MasqueradeActiveSession user={masqueradeUser} />
        )}
      </>
    );
  }

  // Special handling for auth-callback page - render without sidebar/header
  if (location.pathname === '/auth-callback') {
    return (
      <>
        <Outlet />
        {masqueradeUser?.id && (
          <MasqueradeActiveSession user={masqueradeUser} />
        )}
      </>
    );
  }

  const showDemoIntake = (IS_ANONYMOUS_APP || demoMode) && !isAuthenticated;

  // Render outlet for nested routes (MainAppLayout will be rendered here)
  return (
    <>
      <WorkspaceSyncOverlay />
      <Outlet />
      {/* Post-onboarding: welcome modal first, then subscription modal */}
      {showWelcomeFirst && !currentUser?.subscription && (
        <WelcomeModal
          onClose={() => {
            setShowWelcomeFirst(false);
            localStorage.removeItem('leo_welcome_pending');
          }}
          hideContent={postOnboardingHidden}
          source="onboarding_redirect"
        />
      )}
      {!showWelcomeFirst && (
        <SubscriptionRequiredModal
          open={isSubscriptionModalOpen}
          onClose={() => {
            // Only allow closing if user has a subscription
            if (currentUser?.subscription) {
              setIsSubscriptionModalOpen(false);
            }
          }}
        />
      )}
      {showDemoIntake && (
        <Suspense>
          <DemoIntakeOverlay />
        </Suspense>
      )}
    </>
  );
};

export default ProtectedLayout;
